import {
  find,
  findIndex,
  forEach,
  get,
  includes,
  isEmpty,
  isFinite,
  isNaN,
  isNil,
  isNumber,
  isString,
  map,
  round,
  toNumber,
  toString,
  isBoolean
} from 'lodash';
import turf from '@turf/turf';
import chroma from 'chroma-js';

import { PROP_DATA_TYPE } from '../api/Layers/Layers';

const DETECTED_VALUES_LIMIT = 5000;
const EMPTY_VALUE = 'read_as_empty_value';
/**
 * @typedef {object} FeatureCollection
 */

/**
 * Checks the data type for the property value.
 *
 * @param {*} propValue - The property value to check.
 * @returns {string} The data type for the value. One of [nil, number or string].
 */
const checkPropDataType = propValue => {
  if (isNil(propValue)) return PROP_DATA_TYPE.NIL;
  if (isBoolean(propValue)) return PROP_DATA_TYPE.BOOL;
  if (isFinite(toNumber(propValue))) return PROP_DATA_TYPE.NUMBER;
  if (isString(propValue)) return PROP_DATA_TYPE.STRING;
  return PROP_DATA_TYPE.STRING;
};

/**
 * Parses a GeoJSON Feature Collection and returns an array that contains all its
 * properties with min and max values.
 *
 * @param {FeatureCollection} featureCollection - GeoJSON FeatureCollection.
 * @returns {object} An object containing the arrays of propetries and geometries.
 */
export const parseGeoJSONProperties = featureCollection => {
  // turf.collectionOf(featureCollection, 'Polygon', 'featureCollectionPoints2KML');
  const geojsonProperties = [];
  const geojsonGeometries = [];
  turf.featureEach(featureCollection, currentFeature => {
    const finalPropertiesList = map(geojsonProperties, property => property.name);
    const currentProperties = get(currentFeature, 'properties', {});
    forEach(currentProperties, (value, prop) => {
      const currentValue = value;
      const currentStringValue = toString(value);
      if (includes(finalPropertiesList, prop)) {
        const finalProperty = find(geojsonProperties, property => property.name === prop);
        if (isNil(currentValue)) return;

        if (finalProperty.dataType === PROP_DATA_TYPE.NIL)
          finalProperty.dataType = checkPropDataType(currentValue);

        switch (finalProperty.dataType) {
          case PROP_DATA_TYPE.STRING:
            if (isNil(finalProperty.max)) finalProperty.max = currentStringValue;
            if (isNil(finalProperty.min)) finalProperty.min = currentStringValue;
            if (finalProperty.max < currentStringValue) finalProperty.max = currentStringValue;
            else if (finalProperty.min > currentStringValue) finalProperty.min = currentStringValue;
            break;
          case PROP_DATA_TYPE.NUMBER:
            if (isNil(finalProperty.max)) finalProperty.max = toNumber(currentValue);
            if (isNil(finalProperty.min)) finalProperty.min = toNumber(currentValue);
            if (toNumber(finalProperty.max) < toNumber(currentValue))
              finalProperty.max = toNumber(currentValue);
            else if (toNumber(finalProperty.min) > toNumber(currentValue))
              finalProperty.min = toNumber(currentValue);
            break;
          case PROP_DATA_TYPE.BOOL:
            finalProperty.max = true;
            finalProperty.min = false;
            break;
          default:
            console.error('The data type does not exist');
            break;
        }
      } else if (checkPropDataType(currentValue) === PROP_DATA_TYPE.STRING) {
        geojsonProperties.push({
          name: prop,
          max: currentStringValue,
          min: currentStringValue,
          dataType: checkPropDataType(currentValue)
        });
      } else {
        geojsonProperties.push({
          name: prop,
          max: currentValue,
          min: currentValue,
          dataType: checkPropDataType(currentValue)
        });
      }
    });
    let geometryType = turf.getType(currentFeature);
    if (includes(geometryType, 'Multi')) geometryType = geometryType.replace('Multi', '');
    if (!includes(geojsonGeometries, geometryType)) geojsonGeometries.push(geometryType);
  });
  return { geojsonProperties, geojsonGeometries };
};

/**
 * Gets a GeoJSON Feature Collection and the name of one of its properties, and returns an array
 * that contains all the values of that property and the number of times each value appears.
 *
 * @param {FeatureCollection} featureCollection - GeoJSON FeatureCollection.
 * @param {string} selectedProperty - The GeoJSON Property to extract the values from.
 * @returns {object[]} The values of the property and the number of ocurrences each value has among
 * all the features of the Feature Collection.
 */
export const parseFeaturePropertyValues = (featureCollection, selectedProperty) => {
  const valuesToReturn = [];
  const detectedValues = [];

  if (!isEmpty(selectedProperty)) {
    turf.propEach(featureCollection, currentProperties => {
      if (detectedValues.length > DETECTED_VALUES_LIMIT)
        throw new Error('Too many values detected');

      let currentValue = currentProperties[selectedProperty];
      if (!isNumber(currentValue) && isEmpty(currentValue) && !isBoolean(currentValue))
        currentValue = EMPTY_VALUE;

      if (!includes(detectedValues, currentValue)) {
        detectedValues.push(currentValue);
        valuesToReturn.push({
          value: currentValue,
          count: 1,
          index: detectedValues.length,
          color: '#fff'
        });
      } else {
        const valueToUpdate = find(
          valuesToReturn,
          valueToReturn => valueToReturn.value === currentValue
        );
        valueToUpdate.count += 1;
      }
    });
  }

  return valuesToReturn;
};

/**
 * A helper function that computes class breaks for you, based on data.
 * It supports the modes equidistant (e), quantile (q), logarithmic (l), and k-means (k).
 *
 * @param {object} arg - The object argument.
 * @param {FeatureCollection} arg.featureCollection - GeoJSON FeatureCollection.
 * @param {string} arg.selectedProperty - The GeoJSON Property to extract the values from.
 * @param {string} [arg.mode=q] - The modes for breaking the data.
 * @param {string} arg.numIntervarls - The GeoJSON Property to extract the values from.
 * @returns {object} Returns the limits for the data range broken in the corresponding mode and
 * the count for each interval in the range.
 */
export const getDataRangeLimitsAndCount = ({
  featureCollection,
  selectedProperty,
  mode = 'q',
  numIntervarls
}) => {
  const dataValues = [];

  turf.propEach(featureCollection, currentProperties => {
    const currentValue = currentProperties[selectedProperty];
    dataValues.push(Number(currentValue));
  });

  const dataLimits = chroma.limits(dataValues, mode, numIntervarls);

  const countForLegend = [];

  forEach(dataLimits, (value, index, collection) => {
    if (index < collection.length - 1)
      countForLegend.push({
        value: `${round(value)} - ${round(collection[index + 1])}`,
        count: 0,
        index: index + 1
      });
    else if (includes(dataValues, NaN))
      countForLegend.push({
        value: EMPTY_VALUE,
        count: 0,
        index: index + 1
      });
  });

  forEach(dataValues, dataValue => {
    if (isNaN(dataValue) && find(countForLegend, ['value', EMPTY_VALUE])) {
      find(countForLegend, ['value', EMPTY_VALUE]).count += 1;
    } else {
      let bucketNumber = findIndex(
        dataLimits,
        (dataLimit, index) => dataValue >= dataLimit && dataValue < dataLimits[index + 1]
      );
      if (bucketNumber < 0 || bucketNumber === countForLegend.length)
        bucketNumber = countForLegend.length - 1;

      countForLegend[bucketNumber].count += 1;
    }
  });

  return { dataLimits, countForLegend };
};
